/**
 * Automatic input classification.
 *
 * Detection is deterministic and cheap so it can run both in the browser (for
 * an instant hint) and in the Worker (authoritative). A manual override from
 * the caller always wins over the automatic result.
 */

import { parse as parseYaml } from 'yaml';
import type { DetectionResult, InputType, InputTypeSelection } from './types';

const HTTP_METHODS = [
  'GET',
  'POST',
  'PUT',
  'PATCH',
  'DELETE',
  'HEAD',
  'OPTIONS',
  'TRACE',
  'CONNECT',
];

const STACK_TRACE_SIGNALS: Array<{ re: RegExp; signal: string; weight: number }> = [
  { re: /Traceback [(]most recent call last[)]/, signal: 'python traceback header', weight: 90 },
  {
    re: /^\s+at\s+[^\s()]+\s+[(]?[^)]*\.(?:js|ts|mjs|cjs|tsx|jsx):\d+:\d+[)]?/m,
    signal: 'javascript stack frame',
    weight: 80,
  },
  { re: /^\s+at\s+[\w$.<>]+\.[\w$<>]+\s+[(]/m, signal: 'java stack frame', weight: 70 },
  { re: /Exception in thread "/, signal: 'java uncaught exception', weight: 85 },
  { re: /^\s+at\s+.*\.(java|kt):\d+[)]/m, signal: 'jvm stack frame', weight: 85 },
  { re: /File "[^"]+", line \d+, in /, signal: 'python frame', weight: 70 },
  {
    re: /\bat (?:async )?[\w$.<>]+ [(].*:\d+:\d+[)]/m,
    signal: 'node stack frame with file',
    weight: 60,
  },
  { re: /at [\w$.]+ in [^\n]+:line \d+/i, signal: '.NET stack frame', weight: 80 },
  { re: /from [^\n]+\.rb:\d+:in /, signal: 'ruby stack frame', weight: 80 },
  { re: /goroutine \d+ \[\w+\]:/, signal: 'go goroutine dump', weight: 85 },
];

const ERROR_TEXT_SIGNALS: Array<{ re: RegExp; signal: string; weight: number }> = [
  { re: /\bECONNREFUSED\b/, signal: 'ECONNREFUSED', weight: 45 },
  { re: /\bETIMEDOUT\b|socket hang up|timed? ?out/i, signal: 'timeout wording', weight: 40 },
  { re: /\bENOTFOUND\b|getaddrinfo/i, signal: 'DNS resolution failure wording', weight: 45 },
  { re: /\bEAI_AGAIN\b/, signal: 'transient DNS failure wording', weight: 45 },
  {
    re: /\b(?:401|403|404|409|422|429|500|502|503|504)\b/,
    signal: 'HTTP status code in text',
    weight: 25,
  },
  {
    re: /\b(?:invalid_api_key|invalid_token|token_expired|expired_token)\b/i,
    signal: 'auth error code',
    weight: 35,
  },
  {
    re: /\brate limit|too many requests|quota exceeded\b/i,
    signal: 'rate-limit wording',
    weight: 30,
  },
  {
    re: /^\s*(?:Error|TypeError|ReferenceError|SyntaxError|AxiosError|HttpError|ApiError)\b/m,
    signal: 'error object prefix',
    weight: 30,
  },
  { re: /\berror\b[\s\S]{0,60}\bmessage\b/i, signal: 'error/message phrasing', weight: 20 },
  { re: /\bcertificate\b|\bself.?signed\b|\bTLS\b|\bSSL\b/i, signal: 'TLS wording', weight: 25 },
  { re: /\bCORS\b|Access-Control-Allow-Origin/, signal: 'CORS wording', weight: 30 },
];

const WEBHOOK_EVENT_KEYS = [
  'event',
  'event_type',
  'eventType',
  'type',
  'webhook',
  'topic',
  'notification_type',
];

const WEBHOOK_ENVELOPE_KEYS = ['data', 'payload', 'object', 'resource', 'body'];

const SIGNATURE_HEADER_RE =
  /^(?:x[-_]?[a-z0-9-]*signature|x[-_]?hub[-_]?signature|stripe-signature|svix-signature|x-signature|signature|authorization-hmac)/im;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function tryJson(text: string): { ok: boolean; value?: unknown } {
  const trimmed = text.trim();
  if (!/^[[{]/.test(trimmed)) return { ok: false };
  try {
    return { ok: true, value: JSON.parse(trimmed) };
  } catch {
    return { ok: false };
  }
}

function tryYaml(text: string): { ok: boolean; value?: unknown } {
  try {
    const value = parseYaml(text) as unknown;
    if (value === null || value === undefined) return { ok: false };
    if (typeof value === 'string' || typeof value === 'number') return { ok: false };
    return { ok: true, value };
  } catch {
    return { ok: false };
  }
}

function curlScore(text: string): { score: number; signals: string[] } {
  const signals: string[] = [];
  let score = 0;
  const head = text.trimStart();
  if (/^curl(?:\s|$)/.test(head)) {
    score += 85;
    signals.push('input starts with the curl command name');
  }
  if (/(?:^|\n)\s*curl\s+(-[A-Za-z]|--)/.test(text)) {
    score += 30;
    signals.push('curl invocation with CLI flags');
  }
  if (/(?:^|\s)-H\s+['"]/.test(text)) {
    score += 15;
    signals.push('curl -H header flag');
  }
  if (/(?:^|\s)(?:--data|-d|--data-raw|--data-binary|--form|-F)\s/.test(text)) {
    score += 15;
    signals.push('curl body or form flag');
  }
  if (/(?:^|\s)(?:--request|-X)\s+[A-Z]+/.test(text)) {
    score += 10;
    signals.push('curl method flag');
  }
  if (/\\(?=\s*$)/m.test(text) && /curl\s/.test(text)) {
    score += 10;
    signals.push('line continuations typical of multi-line curl commands');
  }
  return { score, signals };
}

function httpResponseScore(text: string): { score: number; signals: string[] } {
  const head = text.trimStart();
  const match = /^HTTP\/(\d\.\d)\s+(\d{3})(?:\s+([^\r\n]*))?/.exec(head);
  if (match) {
    return {
      score: 95,
      signals: [`status line HTTP/${match[1]} ${match[2]}`],
    };
  }
  if (/^HTTP\/\d(\.\d)?\s+\d{3}/m.test(head)) {
    return { score: 90, signals: ['HTTP status line appears later in the input'] };
  }
  return { score: 0, signals: [] };
}

function httpRequestScore(text: string): { score: number; signals: string[] } {
  const signals: string[] = [];
  let score = 0;
  const head = text.trimStart();

  const withVersion = new RegExp(
    `^(${HTTP_METHODS.join('|')})\\s+(\\S+)\\s+HTTP/\\d\\.\\d`,
    'm',
  ).exec(head);
  if (withVersion) {
    return {
      score: 92,
      signals: [`request line ${withVersion[1]} ${withVersion[2]} with HTTP version`],
    };
  }

  const withoutVersion = new RegExp(`^(${HTTP_METHODS.join('|')})\\s+(\\S+)\\s*$`, 'm').exec(head);
  if (withoutVersion && /^[A-Za-z0-9-]+:\s*.+$/m.test(head)) {
    score += 70;
    signals.push(
      `bare request line ${withoutVersion[1]} ${withoutVersion[2]} followed by header lines`,
    );
  } else if (withoutVersion) {
    score += 25;
    signals.push(`input begins with an HTTP method token (${withoutVersion[1]})`);
  }

  if (/^Host:\s*\S+/m.test(head)) {
    score += 20;
    signals.push('Host header present');
  }
  if (/^Content-Type:\s*\S+/m.test(head)) {
    score += 8;
    signals.push('Content-Type header present');
  }
  if (/^Accept:\s*\S+/m.test(head)) {
    score += 6;
    signals.push('Accept header present');
  }
  return { score, signals };
}

function stackTraceScore(text: string): { score: number; signals: string[] } {
  let score = 0;
  const signals: string[] = [];
  for (const { re, signal, weight } of STACK_TRACE_SIGNALS) {
    if (re.test(text)) {
      score += weight;
      signals.push(signal);
    }
  }
  return { score: Math.min(score, 100), signals };
}

function errorTextScore(text: string): { score: number; signals: string[] } {
  let score = 0;
  const signals: string[] = [];
  for (const { re, signal, weight } of ERROR_TEXT_SIGNALS) {
    if (re.test(text)) {
      score += weight;
      signals.push(signal);
    }
  }
  return { score: Math.min(score, 90), signals };
}

export interface JsonClassification {
  score: number;
  signals: string[];
  value?: unknown;
}

export function classifyJson(text: string): JsonClassification {
  const parsed = tryJson(text);
  if (!parsed.ok) return { score: 0, signals: [] };

  const signals = ['input parses as JSON'];
  let score = 55;
  const value = parsed.value;

  if (isPlainObject(value)) {
    signals.push('JSON root is an object');
    const keys = Object.keys(value);
    const eventKey = keys.find((k) => WEBHOOK_EVENT_KEYS.includes(k));
    const envelopeKey = keys.find((k) => WEBHOOK_ENVELOPE_KEYS.includes(k));
    if (eventKey) signals.push(`contains a \`${eventKey}\` event discriminator`);
    if (envelopeKey) signals.push(`contains a \`${envelopeKey}\` payload envelope`);

    if (eventKey && envelopeKey) {
      score += 30;
      signals.push('looks like a webhook envelope');
    }
    if (keys.includes('errors') || keys.includes('error')) {
      score -= 5;
      signals.push('contains an error field, which suggests an API error body');
    }
  } else if (Array.isArray(value)) {
    signals.push('JSON root is an array');
    score += 10;
  }

  if (SIGNATURE_HEADER_RE.test(text)) {
    signals.push('a webhook signature header is present');
  }
  return { score: Math.min(score, 95), signals, value };
}

export interface YamlClassification {
  score: number;
  signals: string[];
  value?: unknown;
}

export function classifyYaml(text: string): YamlClassification {
  if (tryJson(text).ok) return { score: 0, signals: [] };
  if (/^\s*(OPENAPI|openapi|swagger)\s*:/m.test(text)) {
    const parsed = tryYaml(text);
    return {
      score: 80,
      signals: ['YAML document with an OpenAPI/Swagger version key'],
      value: parsed.value,
    };
  }
  const candidates = [/^\s{0,2}[A-Za-z_][\w.-]*\s*:\s*(?:\S|$)/m, /^\s*-\s+\w/m];
  const hits = candidates.filter((re) => re.test(text)).length;
  if (hits === 0) return { score: 0, signals: [] };
  const parsed = tryYaml(text);
  if (!parsed.ok) return { score: 0, signals: [] };
  return {
    score: 45 + hits * 10,
    signals: ['input parses as YAML', 'contains YAML mapping or sequence syntax'],
    value: parsed.value,
  };
}

function webhookScore(
  text: string,
  json: JsonClassification,
): { score: number; signals: string[] } {
  const signals: string[] = [];
  let score = 0;

  if (isPlainObject(json.value)) {
    const keys = Object.keys(json.value);
    const eventKey = keys.find((k) => WEBHOOK_EVENT_KEYS.includes(k));
    const envelopeKey = keys.find((k) => WEBHOOK_ENVELOPE_KEYS.includes(k));
    if (eventKey && envelopeKey) score += 45;
    if (keys.includes('id') && eventKey) {
      score += 10;
      signals.push('carries both an identifier and an event discriminator');
    }
  }

  if (SIGNATURE_HEADER_RE.test(text)) {
    score += 40;
    signals.push('webhook signature header detected');
  }
  if (/^x[-_]?[a-z0-9-]*timestamp:\s*/im.test(text)) {
    score += 10;
    signals.push('webhook timestamp header detected');
  }
  if (/webhook/i.test(text)) {
    score += 15;
    signals.push('the literal word "webhook" appears');
  }
  if (/"(?:livemode|api_version)"\s*:/.test(text)) {
    score += 20;
    signals.push('event envelope metadata fields present');
  }
  return { score: Math.min(score, 100), signals };
}

/**
 * Classify a paste. The returned `confidence` is a 0-100 score, and `signals`
 * explains the decision so the UI can show *why* a type was detected.
 */
export function detectInputType(text: string): DetectionResult {
  const signalsByType = new Map<InputType, string[]>();
  const scores = new Map<InputType, number>();

  const add = (type: InputType, score: number, signals: string[]) => {
    if (score <= 0) return;
    scores.set(type, Math.max(scores.get(type) ?? 0, score));
    signalsByType.set(type, [...(signalsByType.get(type) ?? []), ...signals]);
  };

  if (text.trim().length === 0) {
    return {
      type: 'unknown',
      confidence: 0,
      signals: ['input is empty'],
      alternatives: [],
    };
  }

  const curl = curlScore(text);
  add('curl', curl.score, curl.signals);

  const response = httpResponseScore(text);
  add('http_response', response.score, response.signals);

  const request = httpRequestScore(text);
  add('http_request', request.score, request.signals);

  const stackTrace = stackTraceScore(text);
  add('stack_trace', stackTrace.score, stackTrace.signals);

  const json = classifyJson(text);
  add('json', json.score, json.signals);

  const yaml = classifyYaml(text);
  add('yaml', yaml.score, yaml.signals);

  const webhook = webhookScore(text, json);
  add('webhook', webhook.score, webhook.signals);

  const errorText = errorTextScore(text);
  add('api_error', errorText.score, errorText.signals);

  // Disambiguation: a valid JSON body dominates generic error text.
  if (json.score >= 55) {
    const errScore = errorText.score;
    if (errScore > 0) scores.set('api_error', Math.max(0, Math.min(errScore, json.score - 15)));
  }

  // A webhook envelope is a JSON document, so lower the json candidate when the
  // webhook score clearly wins. This must *overwrite* rather than take a max.
  const webhookScoreValue = scores.get('webhook') ?? 0;
  if (webhookScoreValue >= 60) {
    const jsonScoreValue = scores.get('json') ?? 0;
    scores.set('json', Math.max(0, Math.min(jsonScoreValue, webhookScoreValue - 25)));
  }

  const ranked = [...scores.entries()]
    .filter(([, score]) => score > 0)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));

  if (ranked.length === 0) {
    return {
      type: 'unknown',
      confidence: 20,
      signals: ['no strong structural signal; treating as unstructured API error text'],
      alternatives: [{ type: 'api_error', confidence: 15 }],
    };
  }

  const [topType, topScore] = ranked[0]!;
  const alternatives = ranked.slice(1, 4).map(([type, score]) => ({ type, confidence: score }));
  const signals = dedupe(signalsByType.get(topType) ?? []);
  if (topType === 'webhook') {
    signals.unshift(
      'classified as a webhook delivery (an event discriminator plus a payload envelope)',
    );
  }

  return {
    type: topType,
    confidence: topScore,
    signals,
    alternatives,
  };
}

/**
 * Resolve a caller-supplied selection against the automatic detection.
 * Manual overrides are honoured (the spec requires an override), but we keep
 * the detection result so the UI can show the disagreement.
 */
export function resolveInputType(
  text: string,
  selection: InputTypeSelection,
): { final: InputType; detected: DetectionResult; source: 'auto' | 'manual'; overridden: boolean } {
  const detected = detectInputType(text);
  if (selection === 'auto') {
    return { final: detected.type, detected, source: 'auto', overridden: false };
  }
  return {
    final: selection,
    detected,
    source: 'manual',
    overridden: selection !== detected.type,
  };
}

function dedupe(values: string[]): string[] {
  return [...new Set(values)];
}
